<template>
  <a-tree-select
    v-model:value="innerValue"
    :tree-data="normalizedTree"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :show-search="showSearch"
    :tree-checkable="treeCheckable"
    :tree-default-expand-all="treeDefaultExpandAll"
    :tree-node-filter-prop="treeNodeFilterProp"
    :multiple="multiple"
    :max-tag-count="maxTagCount"
    :status="status"
    :loading="loading"
    :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }"
    class="ant-tree-select"
    @change="onChange"
    @search="onSearch"
  />
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";
import type { TreeSelectOption } from "./types";

type ValueType = string | number | Array<string | number> | null;

interface Props {
  modelValue?: ValueType;
  treeData?: TreeSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  showSearch?: boolean;
  treeCheckable?: boolean;
  treeDefaultExpandAll?: boolean;
  multiple?: boolean;
  loading?: boolean;
  maxTagCount?: number | "responsive";
  status?: "" | "error" | "warning";
  treeNodeFilterProp?: string;
  fieldNames?: { label?: string; value?: string; children?: string };
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  treeData: () => [],
  placeholder: "Chọn...",
  disabled: false,
  size: "middle",
  allowClear: true,
  showSearch: true,
  treeCheckable: false,
  treeDefaultExpandAll: false,
  multiple: false,
  loading: false,
  maxTagCount: "responsive",
  status: "",
  treeNodeFilterProp: "title",
  fieldNames: () => ({ label: "title", value: "value", children: "children" }),
});

const emit = defineEmits<{
  (e: "update:modelValue", value: ValueType): void;
  (e: "change", value: ValueType, label?: any, extra?: any): void;
  (e: "search", value: string): void;
}>();

const mapNode = (node: TreeSelectOption): any => ({
  ...node,
  title: node.title ?? node.label ?? String(node.value),
  value: node.value,
  key: node.key ?? node.value,
  disabled: node.disabled,
  selectable: node.selectable,
  children: node.children?.map(mapNode),
});

const normalizedTree = computed(() => (props.treeData || []).map(mapNode));

const innerValue = computed({
  get: () => props.modelValue,
  set: (val: ValueType) => {
    emit("update:modelValue", val);
  },
});

const onChange = (val: ValueType, label?: any, extra?: any) => {
  emit("update:modelValue", val);
  emit("change", val, label, extra);
};

const onSearch = (val: string) => emit("search", val);
</script>

<style scoped>
.ant-tree-select {
  width: 100%;
}
</style>

<!--
  Hướng dẫn sử dụng — Tree Select
  Global: <h-treeSelect v-model="cat" :tree-data="tree" />
  Import: import { TreeSelect } from '@/components/ant/select'

  tree-data: { title|label, value, children?, disabled? }[]
  Props: modelValue, treeData, treeCheckable, multiple, showSearch...
  Events: update:modelValue, change, search
  Docs: src/components/ant/select/README.md
-->
